import { debounce } from "lodash";
import { APP_CONFIG } from "../constants/constants";
import { routeUrls } from "../navigation/routeUrls";
import { ColorRGB } from "../types/common";
import { localStorageKeys } from "./localStorageItems";

export type TSideEffectLogOutParams = {
  redirect?: boolean;
  cause?: "session-expire" | null; // important flag, used to show session expired popup
};

// TODO: the `sideEffectLogOut` can possibly be moved to `authLogoutService.ts` for sanity
export const sideEffectLogOut = ({
  redirect = true,
  cause = null, // important flag, used to show session expired popup
}: TSideEffectLogOutParams) => {
  if (APP_CONFIG.DEBUG.ALL)
    console.log("[sideEffectLogOut] Logging the user out!");
  localStorage.removeItem(localStorageKeys.loggedInUserData);
  localStorage.removeItem(localStorageKeys.user);
  localStorage.removeItem(localStorageKeys.user_auth_type);
  localStorage.removeItem(localStorageKeys.azureIdToken);
  localStorage.removeItem(localStorageKeys.azureTokenExpiry);
  localStorage.removeItem(localStorageKeys.accessToken);
  localStorage.removeItem(localStorageKeys.ownerOrganization);
  localStorage.removeItem(localStorageKeys.user_profile);
  localStorage.removeItem(localStorageKeys.chatPresence);
  localStorage.removeItem(localStorageKeys.camerasAdminVMAPIURLBase);
  localStorage.removeItem(localStorageKeys.camerasAdminVMAPIKey);
  localStorage.removeItem(localStorageKeys.camerasAdminVMToken);
  // remove sysadmin data from session storage
  localStorage.removeItem(`sa_${localStorageKeys.loggedInUserData}`);
  localStorage.removeItem(`sa_${localStorageKeys.user}`);
  localStorage.removeItem(`sa_${localStorageKeys.user_auth_type}`);
  localStorage.removeItem(`sa_${localStorageKeys.azureIdToken}`);
  localStorage.removeItem(`sa_${localStorageKeys.azureTokenExpiry}`);
  localStorage.removeItem(`sa_${localStorageKeys.user_profile}`);
  localStorage.removeItem(`sa_${localStorageKeys.accessToken}`);
  localStorage.removeItem(`sa_${localStorageKeys.ownerOrganization}`);
  localStorage.removeItem(`sa_${localStorageKeys.camerasAdminVMAPIURLBase}`);
  localStorage.removeItem(`sa_${localStorageKeys.camerasAdminVMAPIKey}`);
  localStorage.removeItem(`sa_${localStorageKeys.camerasAdminVMToken}`);
  // remove column-specific storage keys
  const lsItems = { ...localStorage };
  for (const lsItemKey in lsItems) {
    if (lsItemKey.startsWith("columns")) {
      localStorage.removeItem(lsItemKey);
    }
  }
  if (cause === "session-expire") {
    localStorage.setItem(
      localStorageKeys.pageBeforeSessionExpire,
      window.location.pathname
    ); // Save the current page before logout
    // also set the session got expired flag - used to display singular session expired message
    localStorage.setItem(localStorageKeys.sessionGotExpired, "1");
  }
  if (redirect) {
    // trigger a custom event upon logout
    const event = new CustomEvent("logout", { detail: { cause } });
    document.dispatchEvent(event);
    // redirect after 100ms
    setTimeout(() => {
      window.location.href = routeUrls.landingPage;
    }, 100);
  }
};

export const asyncLoadScript = (
  src: string,
  id: string,
  callback: () => void,
  additionalProps?: any
) => {
  const existingScript = document.getElementById(id);

  if (!existingScript) {
    const script = document.createElement("script");
    script.src = src;
    script.id = id;
    script.async = true;
    script.defer = true;
    if (typeof additionalProps === "object") {
      for (const prop in additionalProps) {
        script.setAttribute(prop, additionalProps[prop]);
      }
    }
    document.body.appendChild(script);

    script.onload = () => {
      if (callback) callback();
    };
  }

  if (existingScript && callback) callback();
};

export const removeScript = (id: string) => {
  const existingScript = document.getElementById(id);
  if (existingScript) existingScript.remove();
};

export const hexToRgb = (
  hex: string,
  defaultValue: ColorRGB = APP_CONFIG.MAPS.DEFAULTS.ZONE_COLOR
) => {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? ([
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ] as ColorRGB)
    : defaultValue;
};

function componentToHex(c: number) {
  var hex = c.toString(16);
  return hex.length == 1 ? "0" + hex : hex;
}

export const rgbToHex = (rgbArr: number[]) => {
  return (
    "#" +
    componentToHex(rgbArr[0]) +
    componentToHex(rgbArr[1]) +
    componentToHex(rgbArr[2])
  );
};

/**
 * @param {Object} object
 * @param {string} key
 * @return {any} value
 */
export const getParameterCaseInsensitive = (
  object: { [key: string]: any },
  key: string
) => {
  const asLowercase = key.toLowerCase();
  const foundKey = Object.keys(object).find(
    (k) => k.toLowerCase() === asLowercase
  );
  return foundKey ? object[foundKey] : undefined;
};

// source: https://stackoverflow.com/a/16436975/1268350
export function arraysEqual(a: string[], b: string[]) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // sort both arrays
  const sortedA = a.slice().sort();
  const sortedB = b.slice().sort();

  // compare sorted arrays
  for (var i = 0; i < sortedA.length; ++i) {
    if (sortedA[i] !== sortedB[i]) return false;
  }

  return true;
}

export function asyncDebounce<F extends (...args: any[]) => Promise<any>>(
  func: F,
  wait?: number
) {
  const resolveSet = new Set<(p: any) => void>();
  const rejectSet = new Set<(p: any) => void>();

  const debounced = debounce((args: Parameters<F>) => {
    func(...args)
      .then((...res) => {
        resolveSet.forEach((resolve) => resolve(...res));
        resolveSet.clear();
      })
      .catch((...res) => {
        rejectSet.forEach((reject) => reject(...res));
        rejectSet.clear();
      });
  }, wait);

  return (...args: Parameters<F>): ReturnType<F> =>
    new Promise((resolve, reject) => {
      resolveSet.add(resolve);
      rejectSet.add(reject);
      debounced(args);
    }) as ReturnType<F>;
}

export const basicDebounce: any = (callback: any, wait: any) => {
  let timeoutId: any = null;
  return (...args: any) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      callback(...args);
    }, wait);
  };
};
